import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ScheduleFormModal = ({ isOpen, onClose, onSave, schedule = null, routes = [] }) => {
  const [formData, setFormData] = useState({
    route_id: '',
    start_time: '',
    end_time: '',
    duration_minutes: '',
    frequency_minutes: '',
    days_mask: []
  });

  const [errors, setErrors] = useState({});

  const daysOfWeek = [
    { value: 'monday', label: 'Pazartesi' },
    { value: 'tuesday', label: 'Salı' },
    { value: 'wednesday', label: 'Çarşamba' },
    { value: 'thursday', label: 'Perşembe' },
    { value: 'friday', label: 'Cuma' },
    { value: 'saturday', label: 'Cumartesi' },
    { value: 'sunday', label: 'Pazar' }
  ];

  useEffect(() => {
    if (schedule) {
      setFormData({
        route_id: schedule?.route_id,
        start_time: schedule?.start_time,
        end_time: schedule?.end_time,
        duration_minutes: schedule?.duration_minutes,
        frequency_minutes: schedule?.frequency_minutes,
        days_mask: schedule?.days_mask || []
      });
    } else {
      setFormData({
        route_id: '',
        start_time: '',
        end_time: '',
        duration_minutes: '',
        frequency_minutes: '',
        days_mask: []
      });
    }
    setErrors({});
  }, [schedule, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.route_id) {
      newErrors.route_id = 'Rota seçimi zorunludur';
    }

    if (!formData?.start_time) {
      newErrors.start_time = 'Başlangıç saati zorunludur';
    }

    if (!formData?.end_time) {
      newErrors.end_time = 'Bitiş saati zorunludur';
    }

    if (formData?.start_time && formData?.end_time && formData?.start_time >= formData?.end_time) {
      newErrors.end_time = 'Bitiş saati başlangıç saatinden sonra olmalıdır';
    }

    if (!formData?.duration_minutes || formData?.duration_minutes <= 0) {
      newErrors.duration_minutes = 'Geçerli bir süre giriniz';
    }

    if (!formData?.frequency_minutes || formData?.frequency_minutes <= 0) {
      newErrors.frequency_minutes = 'Geçerli bir frekans giriniz';
    }

    if (formData?.days_mask?.length === 0) {
      newErrors.days_mask = 'En az bir gün seçmelisiniz';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      days_mask: prev?.days_mask?.includes(day)
        ? prev?.days_mask?.filter(d => d !== day)
        : [...prev?.days_mask, day]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-2000 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-card rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <h2 className="text-xl md:text-2xl font-bold text-foreground font-heading">
            {schedule ? 'Programı Düzenle' : 'Yeni Program Ekle'}
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-smooth active-press"
            aria-label="Kapat"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6">
          <Select
            label="Rota"
            required
            options={routes?.map(route => ({
              value: route?.id,
              label: route?.route_name
            }))}
            value={formData?.route_id}
            onChange={(value) => setFormData(prev => ({ ...prev, route_id: value }))}
            error={errors?.route_id}
            placeholder="Rota seçiniz"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Başlangıç Saati"
              type="time"
              required
              value={formData?.start_time}
              onChange={(e) => setFormData(prev => ({ ...prev, start_time: e?.target?.value }))}
              error={errors?.start_time}
            />

            <Input
              label="Bitiş Saati"
              type="time"
              required
              value={formData?.end_time}
              onChange={(e) => setFormData(prev => ({ ...prev, end_time: e?.target?.value }))}
              error={errors?.end_time}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Süre (Dakika)"
              type="number"
              required
              min="1"
              value={formData?.duration_minutes}
              onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: e?.target?.value }))}
              error={errors?.duration_minutes}
              description="Rotanın tamamlanma süresi"
            />

            <Input
              label="Frekans (Dakika)"
              type="number"
              required
              min="1"
              value={formData?.frequency_minutes}
              onChange={(e) => setFormData(prev => ({ ...prev, frequency_minutes: e?.target?.value }))}
              error={errors?.frequency_minutes}
              description="Seferler arası bekleme süresi"
            />
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              Çalışma Günleri <span className="text-error">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {daysOfWeek?.map(day => (
                <Checkbox
                  key={day?.value}
                  label={day?.label}
                  checked={formData?.days_mask?.includes(day?.value)}
                  onChange={() => handleDayToggle(day?.value)}
                />
              ))}
            </div>
            {errors?.days_mask && (
              <p className="text-sm text-error">{errors?.days_mask}</p>
            )}
          </div>

          <div className="flex flex-col-reverse md:flex-row gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              İptal
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              iconName="Save"
              iconPosition="left"
            >
              {schedule ? 'Güncelle' : 'Kaydet'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleFormModal;